import { Clear } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Tooltip } from "@mui/material";
import classNames from "classnames";
import { useEffect, useId, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { registerPendingTextEdit } from "../utils/pendingTextEdits";
import "./PiInputField.css";
interface PiInputFieldProps {
  label: string;
  placeholder: string;
  info?: string;
  showInfoButton?: boolean;
  onChange: (newValue: string) => void;
  value?: string;
  disabled?: boolean;
  showSearchIcon?: boolean;
  showCrossIcon?: boolean;
  id?: string;
  /**
   * Access to the underlying input, for callers that need to focus it from
   * outside — the browse dialogs bind "/" to their search field this way.
   */
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  stopPropagation?: boolean;
  debounceTime?: number;
  size?: "default" | "compact";
  square?: boolean;
  type?: React.HTMLInputTypeAttribute;
  min?: string | number;
  step?: string | number;
  error?: boolean;
  errorMessage?: string;
  /**
   * Combobox wiring for a field that filters a listbox elsewhere on screen.
   * The browse dialogs keep DOM focus in the search field and move a purely
   * visual highlight through the results, so without `activeDescendantId` a
   * screen reader is told nothing when the highlight moves.
   */
  controlsId?: string;
  activeDescendantId?: string;
}

export default function PiInputfield(props: PiInputFieldProps) {
  const {
    value,
    disabled,
    showSearchIcon,
    autoFocus,
    showCrossIcon,
    stopPropagation,
    debounceTime = 500,
    size = "default",
    square,
    type = "text",
    min,
    step,
    error = false,
  } = {
    ...props,
  };
  const [inputValue, setInputValue] = useState<string | undefined>(value);
  const ref = useRef<HTMLInputElement | null>(null);
  const pendingCommitCleanupRef = useRef<(() => void) | null>(null);
  const latestOnChangeRef = useRef(props.onChange);
  const generatedId = useId();
  const errorMessageId = props.id
    ? `${props.id}-error`
    : `pi-inputfield-${generatedId}-error`;

  useEffect(() => {
    latestOnChangeRef.current = props.onChange;
  }, [props.onChange]);

  useEffect(() => {
    if (!ref.current || value === undefined) return;
    ref.current.value = value;
  }, [value]);

  useEffect(() => {
    if (!ref.current || inputValue === undefined) return;
    ref.current.value = inputValue;
  }, [inputValue]);

  useEffect(() => {
    if (!autoFocus || disabled) return;

    const focusInput = () => ref.current?.focus();
    focusInput();
    const animationFrameId = window.requestAnimationFrame(focusInput);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [autoFocus, disabled]);

  useEffect(() => {
    return () => {
      pendingCommitCleanupRef.current?.();
    };
  }, []);

  const infoButton = props.showInfoButton ? (
    <div
      className={
        size === "compact"
          ? "pi-inputfield-info pi-inputfield-info-compact"
          : "pi-inputfield-info"
      }
    >
      <Tooltip title={props.info} arrow placement="right">
        <IconButton tabIndex={-1}>
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
    </div>
  ) : (
    ""
  );

  const searchIcon = (
    <div className="pi-inputfield-searchicon">
      <SearchIcon />
    </div>
  );

  const flushPendingValue = () => {
    if (!ref.current) return;

    const currentInputValue = ref.current.value;
    commitValue(currentInputValue);
    setInputValue(currentInputValue);
  };

  const clearPendingFlush = () => {
    pendingCommitCleanupRef.current?.();
    pendingCommitCleanupRef.current = null;
  };

  const commitValue = (nextValue: string) => {
    latestOnChangeRef.current(nextValue);
    clearPendingFlush();
  };

  const registerPendingFlush = () => {
    clearPendingFlush();
    pendingCommitCleanupRef.current = registerPendingTextEdit(flushPendingValue);
  };

  const clearInput = () => {
    setInputValue("");
    props.onChange("");
    if (ref.current) ref.current.value = "";
    clearPendingFlush();
  };

  const crossIcon = (
    <button
      aria-label="cross-icon"
      className="text-[15px] mr-[12px] flex items-center text-creator-text-sub"
      onClick={() => clearInput()}
    >
      <Clear fontSize="inherit" />
    </button>
  );

  const debounced = useDebouncedCallback((nextValue: string) => {
    commitValue(nextValue);
  }, debounceTime);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debounced(e.target.value);
    setInputValue(e.target.value);
    registerPendingFlush();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  return (
    <div
      id={props.id ?? ""}
      className={
        disabled
          ? "pi-inputfield-root pi-inputfield-disabled"
          : `w-full pi-inputfield-root${error ? " pi-inputfield-error" : ""}`
      }
    >
      {props.label && <div className="pi-inputfield-label">{props.label}</div>}
      <div
        className={classNames("pi-inputfield-wrapper", {
          "pi-inputfield-wrapper-compact": size === "compact",
          "pi-inputfield-wrapper-square": square,
        })}
      >
        {showSearchIcon && searchIcon}
        <input
          ref={(element) => {
            ref.current = element;
            if (props.inputRef) props.inputRef.current = element;
          }}
          className="pi-inputfield-input"
          defaultValue={inputValue ? inputValue : ""}
          type={type}
          min={min}
          step={step}
          placeholder={props.placeholder}
          // Only claim to be a combobox when a listbox was actually named;
          // the role without the wiring is worse than no role at all.
          role={props.controlsId ? "combobox" : undefined}
          aria-expanded={props.controlsId ? true : undefined}
          aria-controls={props.controlsId}
          aria-autocomplete={props.controlsId ? "list" : undefined}
          aria-activedescendant={props.activeDescendantId}
          aria-invalid={error || undefined}
          aria-describedby={
            error && props.errorMessage ? errorMessageId : undefined
          }
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={flushPendingValue}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        {showCrossIcon && inputValue && inputValue !== "" && crossIcon}
        {infoButton}
      </div>
      {error && props.errorMessage && (
        <div
          id={errorMessageId}
          className="pi-inputfield-error-message"
          role="alert"
        >
          {props.errorMessage}
        </div>
      )}
    </div>
  );
}
